// ---------------------------------------------------------------------------
// C6: Form Intelligence — Drop-off funnel, field completion, qualification
// ---------------------------------------------------------------------------

import { useState, useEffect } from "react";
import { useFormAnalytics } from "@/hooks/use-creative-api";

export default function FormIntelligence() {
  const [formId, setFormId] = useState("");
  const { data, loading, error, load } = useFormAnalytics();

  useEffect(() => { if (formId) load(formId); }, [formId, load]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-1">Form Intelligence</h1>
      <p className="text-sm text-[var(--color-muted-foreground)] mb-6">Understand how users interact with your forms</p>

      {/* Form selector */}
      <div className="mb-6">
        <input
          type="text"
          value={formId}
          onChange={e => setFormId(e.target.value)}
          placeholder="Enter form ID..."
          className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
        />
      </div>

      {loading && <div className="animate-pulse space-y-4"><div className="h-40 bg-[var(--color-muted)] rounded-lg" /><div className="h-40 bg-[var(--color-muted)] rounded-lg" /></div>}
      {error && <div className="text-[var(--color-destructive)] text-sm">{error}</div>}

      {data && (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-background border rounded-lg">
              <div className="text-2xl font-bold">{data.totalSubmissions}</div>
              <div className="text-xs text-[var(--color-muted-foreground)]">Total Submissions</div>
            </div>
            <div className="p-4 bg-background border rounded-lg">
              <div className="text-2xl font-bold">{data.completionRate}%</div>
              <div className="text-xs text-[var(--color-muted-foreground)]">Completion Rate</div>
            </div>
            <div className="p-4 bg-background border rounded-lg">
              <div className="text-2xl font-bold">{data.avgTimeSeconds}s</div>
              <div className="text-xs text-[var(--color-muted-foreground)]">Avg. Time</div>
            </div>
          </div>

          {/* Drop-off funnel */}
          <div className="bg-background border rounded-lg p-4">
            <h3 className="text-sm font-semibold mb-3">Drop-off Funnel</h3>
            <div className="space-y-2">
              {data.dropOffFunnel.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-[var(--color-muted-foreground)] w-32 truncate">{step.fieldName}</span>
                  <div className="flex-1 bg-[var(--color-muted)] rounded-full h-6 relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-[var(--color-primary)] rounded-full transition-all" style={{ width: `${step.completedPercent}%` }} />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                      {step.completedPercent}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Qualification breakdown */}
          <div className="bg-background border rounded-lg p-4">
            <h3 className="text-sm font-semibold mb-3">Qualification Breakdown</h3>
            <div className="space-y-2">
              {data.qualificationBreakdown.map((seg, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-[var(--color-foreground)]">{seg.segment}</span>
                  <span className="text-[var(--color-muted-foreground)]">{seg.count} ({seg.percent}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
